import type { S3SecurityType } from './chartConfig';

// ─── Perf 10k response ────────────────────────────────────────────────────────

export type Perf10kPoint = {
    date: string;   // 'YYYY-MM-DD'
    value: number;
};

export type Perf10kSeries = {
    name: string;         // 'Fund' | 'ETF compare' | …
    ticker?: string;
    legend_label: string;
    points: Perf10kPoint[];
};

export type Perf10kSummary = {
    start_date: string;   // 'YYYY-MM-DD'
    start_value: number;  // 10000
    end_date: string;
    end_value: number;
    end_display: string;  // pre-formatted, e.g. '12,429'
};

export type Perf10kResponse = {
    schema_version: string;
    security: {
        cfra_id: string;
        id_type: string;
        ticker: string;
        name: string;
        report_type: S3SecurityType;
    };
    chart_type: 'perf10k';
    title: string;
    report_date: string;
    generated_at: string;
    data: {
        summary: Perf10kSummary;
        series: Perf10kSeries[];
    };
    currency: string;
};

// ─── Type guard ───────────────────────────────────────────────────────────────

function isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

export function isPerf10kResponse(data: unknown): data is Perf10kResponse {
    if (!isObject(data)) return false;
    if (data.chart_type !== 'perf10k') return false;
    if (!isObject(data.data)) return false;

    const { summary, series } = data.data as { summary?: unknown; series?: unknown };
    if (!isObject(summary)) return false;
    if (typeof summary.start_date !== 'string' || typeof summary.end_date !== 'string') return false;
    if (typeof summary.start_value !== 'number' || typeof summary.end_value !== 'number') return false;
    if (!Array.isArray(series)) return false;
    return true;
}

export function getPrimarySeries(response: Perf10kResponse): Perf10kSeries | undefined {
    return response.data.series.find((s) => s.name === 'Fund') ?? response.data.series[0];
}

// ─── Shout TNA response (Shares Outstanding + Total Net Assets) ───────────────

export type ShoutTnaPoint = {
    date: string;               // 'YYYY-MM-DD'
    shares_outstanding: number;
    total_net_assets: number;
};

export type ShoutTnaResponse = {
    schema_version: string;
    security: {
        cfra_id: string;
        id_type: string;
        ticker: string;
        name: string;
        report_type: S3SecurityType;
    };
    chart_type: 'shout_tna';
    title: string;
    report_date: string;
    generated_at: string;
    data: {
        // Flat array of daily points — no nested named series and no summary
        // block (cf. Perf10kResponse). Caller derives the default summary from
        // the latest entry.
        series: ShoutTnaPoint[];
    };
    currency: string;
};

export function isShoutTnaResponse(data: unknown): data is ShoutTnaResponse {
    if (!isObject(data)) return false;
    if (data.chart_type !== 'shout_tna') return false;
    if (!isObject(data.data)) return false;

    const series = (data.data as { series?: unknown }).series;
    if (!Array.isArray(series)) return false;
    // Spot-check the first point's shape — if it's well-formed, trust the rest.
    if (series.length === 0) return true;
    const first = series[0];
    if (!isObject(first)) return false;
    if (typeof first.date !== 'string') return false;
    if (typeof first.shares_outstanding !== 'number') return false;
    if (typeof first.total_net_assets !== 'number') return false;
    return true;
}

/** Latest point by ISO date (lexicographic compare works for 'YYYY-MM-DD'). */
export function getLatestShoutTnaPoint(response: ShoutTnaResponse): ShoutTnaPoint | undefined {
    const series = response.data.series;
    if (series.length === 0) return undefined;
    let latest = series[0];
    for (let i = 1; i < series.length; i++) {
        if (series[i].date > latest.date) latest = series[i];
    }
    return latest;
}

// ─── Economic Sector Representation response ──────────────────────────────────

export type EconSectorCategory = {
    sector: string;   // GICS sector name, e.g. 'Information Technology'
    value: number;    // Weighting already scaled to percent (e.g. 34.3, not 0.343)
    display: string;  // Backend-formatted text, e.g. '34.3'
};

export type EconSectorResponse = {
    schema_version: string;
    security: {
        cfra_id: string;
        id_type: string;
        ticker: string;
        name: string;
        report_type: S3SecurityType;
    };
    chart_type: 'econ_sector';
    title: string;
    report_date: string;
    generated_at: string;
    data: {
        // Point-in-time snapshot — no nested series and no per-point date.
        // The single report_date above is the as-of date for every category.
        unit: string;            // 'percent'
        column_headers: string[];
        categories: EconSectorCategory[];
    };
};

export function isEconSectorResponse(data: unknown): data is EconSectorResponse {
    if (!isObject(data)) return false;
    if (data.chart_type !== 'econ_sector') return false;
    if (!isObject(data.data)) return false;

    const categories = (data.data as { categories?: unknown }).categories;
    if (!Array.isArray(categories)) return false;
    if (categories.length === 0) return true;
    const first = categories[0];
    if (!isObject(first)) return false;
    if (typeof first.sector !== 'string') return false;
    if (typeof first.value !== 'number') return false;
    return true;
}

// ─── Credit Quality Breakdown response ────────────────────────────────────────

export type CreditQualityBucket = {
    grade: string;    // Credit grade label, e.g. 'AAA', 'BBB', 'Not Rated'
    value: number;    // Raw weighting in percent (e.g. 0.3 for "< 1")
    display: string;  // Backend-formatted text, e.g. '94.08' or '< 1'
};

export type CreditQualityResponse = {
    schema_version: string;
    security: {
        cfra_id: string;
        id_type: string;
        ticker: string;
        name: string;
        report_type: S3SecurityType;
    };
    chart_type: 'credit_quality';
    title: string;
    report_date: string;
    generated_at: string;
    data: {
        unit: string;
        buckets: CreditQualityBucket[];
    };
};

export function isCreditQualityResponse(data: unknown): data is CreditQualityResponse {
    if (!isObject(data)) return false;
    if (data.chart_type !== 'credit_quality') return false;
    if (!isObject(data.data)) return false;

    const buckets = (data.data as { buckets?: unknown }).buckets;
    if (!Array.isArray(buckets)) return false;
    if (buckets.length === 0) return true;
    const first = buckets[0];
    if (!isObject(first)) return false;
    if (typeof first.grade !== 'string') return false;
    if (typeof first.value !== 'number') return false;
    return true;
}

// ─── Price Performance response (STARS_ENRT) ──────────────────────────────────

export type PricePerformancePoint = {
    date: string;        // 'YYYY-MM-DD'
    price: number;
    pct_change: number;  // already scaled to percent (e.g. 28.5, not 0.285)
};

export type PriceIndexPoint = {
    date: string;
    pct_change: number;
};

export type PriceIndexSeries = {
    label: string;
    points: PriceIndexPoint[];
};

export type TargetPricePoint = {
    date: string;
    value: number;
    pct_change: number;
};

export type StarsRankChangePoint = {
    date: string;
    rank: 1 | 2 | 3 | 4 | 5;
};

export type PricePerformanceResponse = {
    schema_version: string;
    security: {
        cfra_id: string;
        id_type: string;
        ticker: string;
        name: string;
        report_type: S3SecurityType;
    };
    chart_type: 'price_performance';
    title: string;
    report_date: string;
    generated_at: string;
    data: {
        share_price_label: string;
        index_label: string;
        starting_price: number;
        price_axis: { min: number; max: number };
        percent_axis: { min: number; max: number };
        company_price: PricePerformancePoint[];
        index: PriceIndexSeries;
        target_price: TargetPricePoint[];
        stars_rank_changes: StarsRankChangePoint[];
        drop_initiations: unknown[];
    };
    currency: string;
};

export function isPricePerformanceResponse(data: unknown): data is PricePerformanceResponse {
    if (!isObject(data)) return false;
    if (data.chart_type !== 'price_performance') return false;
    if (!isObject(data.data)) return false;

    const d = data.data as Record<string, unknown>;
    if (!Array.isArray(d.company_price)) return false;
    if (!isObject(d.index)) return false;
    if (!Array.isArray((d.index as { points?: unknown }).points)) return false;
    if (!Array.isArray(d.target_price)) return false;
    if (!Array.isArray(d.stars_rank_changes)) return false;

    // Spot-check the first company_price point — if it's well-formed, trust the rest.
    if (d.company_price.length === 0) return true;
    const first = d.company_price[0];
    if (!isObject(first)) return false;
    if (typeof first.date !== 'string') return false;
    if (typeof first.price !== 'number') return false;
    if (typeof first.pct_change !== 'number') return false;
    return true;
}

/** Latest point by ISO date (lexicographic compare works for 'YYYY-MM-DD'). */
export function getLatestPricePerformancePoint(
    response: PricePerformanceResponse,
): PricePerformancePoint | undefined {
    const series = response.data.company_price;
    if (series.length === 0) return undefined;
    let latest = series[0];
    for (let i = 1; i < series.length; i++) {
        if (series[i].date > latest.date) latest = series[i];
    }
    return latest;
}

// ─── Asset Allocation response ────────────────────────────────────────────────

export type AssetAllocationSlice = {
    label: string;          // Asset type label, e.g. 'Domestic', 'Cash'
    value: number;          // Weighting scaled to percent (e.g. 92.8)
    display: string;        // Backend-formatted text already containing `%`, e.g. '92.8%'
    legend_label: string;   // Pre-composed legend string, e.g. 'Domestic - 92.8%'
};

export type AssetAllocationResponse = {
    schema_version: string;
    security: {
        cfra_id: string;
        id_type: string;
        ticker: string;
        name: string;
        report_type: S3SecurityType;
    };
    chart_type: 'asset_allocation';
    title: string;
    report_date: string;
    generated_at: string;
    data: {
        unit: string;
        slices: AssetAllocationSlice[];
    };
};

export function isAssetAllocationResponse(data: unknown): data is AssetAllocationResponse {
    if (!isObject(data)) return false;
    if (data.chart_type !== 'asset_allocation') return false;
    if (!isObject(data.data)) return false;

    const slices = (data.data as { slices?: unknown }).slices;
    if (!Array.isArray(slices)) return false;
    if (slices.length === 0) return true;
    const first = slices[0];
    if (!isObject(first)) return false;
    if (typeof first.label !== 'string') return false;
    if (typeof first.value !== 'number') return false;
    return true;
}

// ─── Summary Credit Rating response ───────────────────────────────────────────

export type SummaryCreditRatingSlice = {
    label: string;          // Rating bucket label, e.g. 'Investment Grade', 'Not Rated'
    value: number;          // Weighting scaled to percent
    display: string;        // Backend-formatted text already containing `%`, e.g. '59.6%'
    legend_label: string;   // Pre-composed legend string, e.g. 'Investment Grade - 59.6%'
};

export type SummaryCreditRatingResponse = {
    schema_version: string;
    security: {
        cfra_id: string;
        id_type: string;
        ticker: string;
        name: string;
        report_type: S3SecurityType;
    };
    chart_type: 'summary_credit_rating';
    title: string;
    report_date: string;
    generated_at: string;
    data: {
        unit: string;
        slices: SummaryCreditRatingSlice[];
    };
};

export function isSummaryCreditRatingResponse(data: unknown): data is SummaryCreditRatingResponse {
    if (!isObject(data)) return false;
    if (data.chart_type !== 'summary_credit_rating') return false;
    if (!isObject(data.data)) return false;

    const slices = (data.data as { slices?: unknown }).slices;
    if (!Array.isArray(slices)) return false;
    if (slices.length === 0) return true;
    const first = slices[0];
    if (!isObject(first)) return false;
    if (typeof first.label !== 'string') return false;
    if (typeof first.value !== 'number') return false;
    return true;
}
